import React, { useState, useMemo, KeyboardEvent, useEffect } from 'react';
import { useUser } from '../../contextManager/user.context';
import { ProfilePatch } from '../../hooks/auth.service';
import { FaTimes } from 'react-icons/fa';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
// --- LAYOUT & BUTTONS ---
import { ProfileDescriptionCard, CardHeader, CardContent, CardFooter } from '../../styles/profile/layout.style';
import { ActionButton } from '../../styles/profile/profile.style';

// --- FIELD COMPONENTS ---
import { EditField, SubheaderContainer, SubheaderLine, SubheaderTitle } from '../../styles/profile/form.style';
import { ManualField, ManualLabel, ManualInput } from './ProfileDetails';
import { TagWrap, Tag, Remove, PreviewViewport, Grid, Cell, OverlayTextarea, ZoomWrapper, RateDisplay, ZoomLabel, OptionsContainer, CheckboxWrapper, CheckboxLabel, CheckboxDescription } from '../../styles/login.style';
import UniversityDropdown from '../register/UniversityDropdown';
import { TimezonePicker } from '../common/TimezonePicker';

// --- DATA CONSTANTS ---
import { cambridgeColleges, classificationOptions, LEVEL_OPTIONS, oxfordColleges, QualificationGroup, SUBJECT_OPTIONS, SubjectEntry } from '../../types/schemas/abc';
import { TableHintSmall } from '../register/InfoBlock';
import {
    aLevelGrades, aLevelSubjectOptions, apGrades, apSubjectOptions,
    cambridgePreUGrades, cambridgePreUSubjectOptions, gcseGrades,
    gcseSubjectOptions, hkdseGrades, ibGrades, ibSubjectOptions,
    indianSchoolCertificateGrades, irishLeavingCertGrades,
    irishLeavingCertSubjectOptions, qualificationOptions, scottishHighersGrades,
    scottishHighersSubjectOptions, singaporeGceALevelGrades
  } from '../../types/schemas/subjectsandgrades';
import StyledDropdown, { OptionGroup } from '../register/StyledDropdown';
import SubjectCombobox from '../common/SubjectCombobox';
import { Field, Label, Control, AddSection, LightButton, GroupContainer, GroupHeader, GroupTitle, EntryRow, TotalScoreContainer, TotalScoreValue, TotalScoreLabel, TotalScoreBreakdown } from '../../styles/login.style';
import { Plus, Trash2 } from 'lucide-react';
import QualificationsDropdown from '../register/QualificationDropdown';
import { CustomCheckbox } from '../messenger/CustomCheckbox';



const uniOptions: ('Oxford' | 'Cambridge')[] = ['Oxford', 'Cambridge'];

const getQualificationDetails = (type: string) => {
    for (const group of qualificationOptions) {
        const option = group.options.find(opt => opt.value === type);
        if (option) return option;
    }
    return null;
  };

const getSubjectOptions = (type: string): OptionGroup[] => {
    switch (type) {
        case 'GCSE':
            return gcseSubjectOptions;
        case 'A-Level':
            return aLevelSubjectOptions;
        case 'Scottish Highers':
            return scottishHighersSubjectOptions;
        case 'Cambridge Pre-U':
            return cambridgePreUSubjectOptions;
        case 'AP':
            return apSubjectOptions;
        case 'Irish Leaving Certificate':
            return irishLeavingCertSubjectOptions;
        case 'IB Diploma':
            return ibSubjectOptions
        default:
            return [];
    }
  };


  const getGradeOptions = (type: string): OptionGroup[] => {
    let grades: string[] = [];
    switch (type) {
        case 'IB Diploma':
            grades = ibGrades;
            break;
        case 'A-Level':
            grades = aLevelGrades;
            break;
        case 'GCSE':
            grades = gcseGrades;
            break;
        case 'AP':
            grades = apGrades;
            break;
        case 'IB Subject':
            grades = ibGrades;
            break;
        case 'Scottish Highers':
            grades = scottishHighersGrades;
            break;
        case 'Cambridge Pre-U':
            grades = cambridgePreUGrades;
            break;
        case 'Irish Leaving Certificate':
            grades = irishLeavingCertGrades;
            break;
        case 'HKDSE':
            grades = hkdseGrades;
            break;
        case 'Indian School Certificate':
            grades = indianSchoolCertificateGrades;
            break;
        case 'Singapore GCE A-Level':
            grades = singaporeGceALevelGrades;
            break;
        default:
            grades = [];
    }

    if (grades.length === 0) return [];

    return [{
        label: 'Available Grades',
        options: grades.map(g => ({ value: g, label: g }))
    }];
  };

  const calculateIbTotal = (group: QualificationGroup): { score: number | null, message: string } => {
    if (!group.entries || group.entries.length === 0) {
        return { score: null, message: 'Add subjects to calculate score.' };
    }

    const tokEntry = group.entries.find(e => e.subject === 'Theory of Knowledge');
    const eeEntry = group.entries.find(e => e.subject === 'Extended Essay');

    if (!tokEntry || !eeEntry || !tokEntry.grade || !eeEntry.grade) {
        return { score: null, message: 'Select grades for TOK and EE.' };
    }

    if (tokEntry.grade === 'E' || eeEntry.grade === 'E') {
        return { score: null, message: 'A grade of E in TOK or EE results in failure.' };
    }
    const tokGrade = tokEntry.grade as IbCoreGrade;
    const eeGrade = eeEntry.grade as IbCoreGrade;

    const bonusPoints = bonusPointsMatrix[tokGrade]?.[eeGrade] ?? 0;

    const subjectEntries = group.entries.filter(
        e => e.subject !== 'Theory of Knowledge' && e.subject !== 'Extended Essay'
    );

    let subjectPoints = 0;
    for (const entry of subjectEntries) {
        const gradePoint = parseInt(entry.grade, 10);
        if (!isNaN(gradePoint)) {
            subjectPoints += gradePoint;
        }
    }
    
    const totalScore = subjectPoints + bonusPoints;
    
    return { score: totalScore, message: `(${subjectPoints} subject points + ${bonusPoints} bonus points)`};
};

const bonusPointsMatrix = {
    'A': { 'A': 3, 'B': 3, 'C': 2, 'D': 2 },
    'B': { 'A': 3, 'B': 2, 'C': 1, 'D': 1 },
    'C': { 'A': 2, 'B': 1, 'C': 1, 'D': 0 },
    'D': { 'A': 2, 'B': 1, 'C': 0, 'D': 0 },
};

type IbCoreGrade = keyof typeof bonusPointsMatrix;

// --- COMPONENT PROPS ---
interface ProfileDescriptionFormProps {
  onSave: (formData: ProfilePatch) => Promise<void>;
  onCancel: () => void;
  onVerificationThreat?: (field: string, isThreatened: boolean) => void;
}


// --- THE EDIT FORM ---
export const ProfileDescriptionForm: React.FC<ProfileDescriptionFormProps> = ({ onSave, onCancel, onVerificationThreat }) => {
  const { userDetails } = useUser();

  const [formData, setFormData] = useState<ProfilePatch>({
    university: userDetails?.university,
    college: userDetails?.college,
    degreeCourse: userDetails?.degreeCourse,
    gradYear: userDetails?.gradYear,
    classification: userDetails?.classification,
    subjects: userDetails?.subjects ?? [],
    levels: userDetails?.levels ?? [],
    expertise: userDetails?.expertise ?? {},
    description: userDetails?.description ?? '',
    qualificationGroups: userDetails?.qualificationGroups ?? [],
    hourlyRate: userDetails?.hourlyRate ?? 30,
    offersFreeFirstLesson: userDetails?.offersFreeFirstLesson ?? false,
    offersFreeIntroCall: userDetails?.offersFreeIntroCall ?? true,
    timezone: userDetails?.timezone ?? '',
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [typeToAdd, setTypeToAdd] = useState('A-Level');

  // ── Verification threat signalling ────────────────────────────
  const isVerified = !!userDetails?.account_verified;

  useEffect(() => {
    if (!onVerificationThreat || !isVerified) return;

    // Check each key field: threatened if it HAD a value originally but is now empty
    const threats: [string, boolean][] = [
      ['Description', !!userDetails?.description && !formData.description?.trim()],
      ['University', !!userDetails?.university && !formData.university],
      ['College', !!userDetails?.college && !formData.college],
      ['Degree course', !!userDetails?.degreeCourse && !formData.degreeCourse?.trim()],
      ['Graduation year', !!userDetails?.gradYear && !formData.gradYear],
      ['Subjects taught', 
        !!(userDetails?.expertise && Object.keys(userDetails.expertise).length > 0) && 
        (!formData.expertise || Object.keys(formData.expertise).length === 0)
      ],
    ];

    threats.forEach(([field, isThreatened]) => {
      onVerificationThreat(field, isThreatened);
    });
  }, [
    formData.description, formData.university, formData.college,
    formData.degreeCourse, formData.gradYear, formData.expertise,
    isVerified, onVerificationThreat, userDetails,
  ]);

  // Clear all threats on unmount (cancel / save navigates away from form)
  useEffect(() => {
    return () => {
      if (!onVerificationThreat) return;
      ['Description', 'University', 'College', 'Degree course', 'Graduation year', 'Subjects taught']
        .forEach(f => onVerificationThreat(f, false));
    };
  }, [onVerificationThreat]);

  const isDirty = useMemo(() => {
    const initialData = {
      university: userDetails?.university,
      college: userDetails?.college,
      degreeCourse: userDetails?.degreeCourse,
      gradYear: userDetails?.gradYear,
      classification: userDetails?.classification,
      subjects: userDetails?.subjects ?? [],
      levels: userDetails?.levels ?? [],
      expertise: userDetails?.expertise ?? {},
      description: userDetails?.description ?? '',
      qualificationGroups: userDetails?.qualificationGroups ?? [],
      hourlyRate: userDetails?.hourlyRate ?? 30,
      offersFreeFirstLesson: userDetails?.offersFreeFirstLesson ?? false,
      offersFreeIntroCall: userDetails?.offersFreeIntroCall ?? true,
  }
    return JSON.stringify(initialData) !== JSON.stringify(formData);
  }, [userDetails, formData]);

const descriptionWordCount = (formData.description ?? '').trim()
  ? (formData.description ?? '').trim().split(/\s+/).length
  : 0;
const isDescriptionOverLimit = descriptionWordCount > 500;

const collegeOptions = formData.university === 'Oxford' ? oxfordColleges : cambridgeColleges;


const rowSubjects = formData.subjects ?? [];
const colLevels = formData.levels ?? [];
const showClassification = formData.gradYear && formData.gradYear < new Date().getFullYear();



const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let finalValue: string | number | undefined = value;
    if (name === 'gradYear') {
      const parsed = parseInt(value, 10);
      finalValue = value === '' || Number.isNaN(parsed) ? undefined : parsed;
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };
  
  const handleUniversityChange = (newUniversity: string) => {
    setFormData(prev => ({
      ...prev,
      university: newUniversity as 'Oxford' | 'Cambridge',
    }));
  };

  const handleSaveClick = async () => {
    if (!isDirty || isSaving) return;
  
    setIsSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };  
  
  const addSubject = (s: string) => {
    if (!s) return;
    const currentSubjects = new Set(formData.subjects ?? []);
    if (currentSubjects.has(s)) return;
    currentSubjects.add(s);
    setFormData(prev => ({ ...prev, subjects: Array.from(currentSubjects) }));
  };

  const removeSubject = (subjectToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      subjects: (prev.subjects ?? []).filter(s => s !== subjectToRemove)
    }));
  };
  
const addLevel = (l: string) => {
  if (!l) return;
  const currentLevels = new Set(formData.levels ?? []);
  if (currentLevels.has(l)) return;
  currentLevels.add(l);
  setFormData(prev => ({ ...prev, levels: Array.from(currentLevels) }));
};

const removeLevel = (levelToRemove: string) => {
  setFormData(prev => ({
    ...prev,
    levels: (prev.levels ?? []).filter(l => l !== levelToRemove)
  }));
};

const toggle = (subject: string, level: string) => {
    setFormData(prev => {
      const currentLevels = prev.expertise?.[subject] ?? [];
      const isTicked = currentLevels.includes(level);
      
      const newLevels = isTicked
        ? currentLevels.filter(l => l !== level)
        : [...currentLevels, level];

      const newExpertise = {
        ...prev.expertise,
        [subject]: newLevels,
      };

      return { ...prev, expertise: newExpertise };
    });
  };

  useEffect(() => {
    if (!formData.subjects || !formData.levels || !formData.expertise) return;

    const currentSubjects = new Set(formData.subjects);
    const currentLevels = new Set(formData.levels);
    const cleanedExpertise = { ...formData.expertise };
    let hasChanged = false;

    for (const subject in cleanedExpertise) {
      if (!currentSubjects.has(subject)) {
        delete cleanedExpertise[subject];
        hasChanged = true;
      } else {
        const originalLevels = cleanedExpertise[subject];
        const cleanedLevels = originalLevels.filter(level => currentLevels.has(level));
        if (cleanedLevels.length !== originalLevels.length) {
          cleanedExpertise[subject] = cleanedLevels;
          hasChanged = true;
        }
      }
    }

    if (hasChanged) {
      setFormData(prev => ({ ...prev, expertise: cleanedExpertise }));
    }
  }, [formData.subjects, formData.levels]);


  const addGroup = () => {
    if (formData.qualificationGroups?.some(g => g.type === typeToAdd)) return;
    const newGroup: QualificationGroup = {
      id: crypto.randomUUID(),
      type: typeToAdd,
      entries: [{ id: crypto.randomUUID(), subject: '', grade: '' }],
    };
    setFormData(prev => ({...prev, qualificationGroups: [...(prev.qualificationGroups ?? []), newGroup]}));

    // Advance the picker to the next un-added qualification so consecutive adds work.
    const added = new Set([
      ...(formData.qualificationGroups?.map(g => g.type) ?? []),
      typeToAdd,
    ]);
    const next = qualificationOptions
      .flatMap(g => g.options)
      .find(opt => !added.has(opt.value));
    if (next) setTypeToAdd(next.value);
  };

  const isTypeAlreadyAdded = !!formData.qualificationGroups?.some(g => g.type === typeToAdd);
  
  const removeGroup = (groupId: string) => {
    setFormData(prev => ({...prev, qualificationGroups: prev.qualificationGroups?.filter(g => g.id !== groupId)}));
  };
  
  const addSubjectToGroup = (groupId: string) => {
    const newGroups = formData.qualificationGroups?.map(g => {
      if (g.id === groupId) {
        const newEntry: SubjectEntry = { id: crypto.randomUUID(), subject: '', grade: '' };
        return { ...g, entries: [...(g.entries ?? []), newEntry] };
      }
      return g;
    });
    setFormData(prev => ({...prev, qualificationGroups: newGroups}));
  };
  
  const updateSubjectEntry = (groupId: string, entryId: string, field: 'subject' | 'grade', value: string) => {
    const newGroups = formData.qualificationGroups?.map(g => {
      if (g.id === groupId) {
          const newEntries = g.entries?.map(e => e.id === entryId ? { ...e, [field]: value } : e);
          return { ...g, entries: newEntries };
      }
      return g;
    });
    setFormData(prev => ({...prev, qualificationGroups: newGroups}));
  };
  
  const updateSingleScore = (groupId: string, value: string) => {
    const newGroups = formData.qualificationGroups?.map(g => g.id === groupId ? { ...g, score: value } : g);
    setFormData(prev => ({...prev, qualificationGroups: newGroups}));
  };
  
  const removeSubjectEntry = (groupId: string, entryId: string) => {
    const newGroups = formData.qualificationGroups?.map(g => {
      if (g.id === groupId) {
          const newEntries = g.entries?.filter(e => e.id !== entryId);
          return { ...g, entries: newEntries };
      }
      return g;
    });
    const filteredGroups = newGroups?.filter(g => (g.entries?.length ?? 0) > 0 || g.score);
    setFormData(prev => ({...prev, qualificationGroups: filteredGroups}));
  };


  
  return (
    <>
      <CardHeader>Edit About & Qualifications</CardHeader>
      <form
        onSubmit={handleSaveClick} 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          flex: '1 1 0%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      > 
      <CardContent>
        <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>College and Degree</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>
        <EditField>
          <ManualLabel>University</ManualLabel>
          <UniversityDropdown
            value={formData.university || ''}
            options={uniOptions}
            onChange={handleUniversityChange}
          />
        </EditField>

        <EditField>
          <ManualLabel>College</ManualLabel>
          <UniversityDropdown
            value={formData.college || ''}
            options={['Select', ...collegeOptions]}
            onChange={(val) => setFormData(prev => ({ ...prev, college: val }))}
          />
        </EditField>

        <ManualField>
          <ManualLabel>Course</ManualLabel>
          <ManualInput
            name="degreeCourse"
            value={formData.degreeCourse || ''}
            placeholder='e.g History BA'
            onChange={handleInputChange}
          />
        </ManualField>

        <ManualField>
          <ManualLabel>Graduation year</ManualLabel>
          <ManualInput
            name="gradYear"
            type="number"
            placeholder="e.g. 2027"
            value={formData.gradYear ?? ''}
            onChange={handleInputChange}
          />
        </ManualField>

        {showClassification && (
          <EditField>
            <ManualLabel>Classification</ManualLabel>
            <UniversityDropdown
              value={formData.classification || ''}
              options={['Select', ...classificationOptions]}
              onChange={(val) => setFormData(prev => ({ ...prev, classification: val }))}
            />
          </EditField>
        )}


<ManualField>
          <ManualLabel>Short Bio (500 words max)</ManualLabel>

          <OverlayTextarea
            as="textarea"
            rows={4}
            name="description"
            value={formData.description || ''}
            onChange={handleInputChange}
            placeholder="Tell students about your teaching style..."
          />
          <small style={{ alignSelf: 'flex-end', color: descriptionWordCount > 500 ? '#dc2626' : undefined }}>
            {descriptionWordCount}/500 words
          </small>
        </ManualField>

        <ManualField>
          <ManualLabel>Timezone</ManualLabel>
          <TimezonePicker
            value={formData.timezone || ''}
            onChange={(tz) => setFormData(prev => ({ ...prev, timezone: tz }))}
          />
        </ManualField>

      <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>Rate</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>

<ManualField>
  <ZoomWrapper profile={true}>
    <ZoomLabel css={{fontWeight: 500, color: '$slate11', marginBottom: '1rem'}}>Hourly Rate (£25-£50)</ZoomLabel>
    <Slider
      min={25}
      max={50}
      step={1}
      value={formData.hourlyRate}
      onChange={(value) => {
        if (typeof value === 'number') {
          setFormData(prev => ({ ...prev, hourlyRate: value }));
        }
      }}
    />
  </ZoomWrapper>
  <RateDisplay>£{formData.hourlyRate} / hour</RateDisplay>

</ManualField> 

<OptionsContainer>
                <CheckboxWrapper>
                    <CustomCheckbox                         
                        checked={!!formData.offersFreeFirstLesson}
                        onChange={() => setFormData(prev => ({...prev, offersFreeFirstLesson: !prev.offersFreeFirstLesson}))}
                        />
                    <CheckboxLabel>Offer first hour free</CheckboxLabel>
                </CheckboxWrapper>

                <CheckboxWrapper>
                <CustomCheckbox                         
                        checked={!!formData.offersFreeIntroCall}
                        onChange={() => setFormData( prev => ({...prev, offersFreeIntroCall: !prev.offersFreeIntroCall }))}
                        />
                    <CheckboxLabel>Offer a free 15-min intro call</CheckboxLabel>
                    <CheckboxDescription>Recommended</CheckboxDescription>
                </CheckboxWrapper>
            </OptionsContainer>


        <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>Subjects</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>


        <ManualField>
          <ManualLabel>Subjects you teach</ManualLabel>
          <SubjectCombobox
            options={SUBJECT_OPTIONS}
            mode="multi"
            exclude={formData.subjects ?? []}
            onSelect={addSubject}
            placeholder="Search or select a subject…"
          />
          <TagWrap>
            {(formData.subjects ?? []).map(s => (
                <Tag key={s} title={s}>
                {s}
                <Remove type="button" onClick={() => removeSubject(s)}>
                    <FaTimes size={10} />
                </Remove>
                </Tag>
            ))}
          </TagWrap>
        </ManualField>

        <ManualField>
            <ManualLabel>Levels you teach</ManualLabel>
            <SubjectCombobox
              options={LEVEL_OPTIONS}
              mode="multi"
              exclude={formData.levels ?? []}
              onSelect={addLevel}
              placeholder="Search or select a level…"
            />
            <TagWrap>
                {(formData.levels ?? []).map(l => (
                <Tag key={l} title={l}>
                    {l}
                    <Remove type="button" onClick={() => removeLevel(l)}>
                    <FaTimes size={10} />
                    </Remove>
                </Tag>
                ))}
            </TagWrap>
        </ManualField>

        {rowSubjects.length && colLevels.length ? (
        <>
        <TableHintSmall css={{
           minWidth: 0,
           whiteSpace: 'normal',
        }}
        />
        <div style={{
          paddingTop: '1rem',
          display: 'grid', 
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
        }}>

        <PreviewViewport>
  
        <Grid
        css={{
            gridTemplateColumns: `minmax(140px,1fr) repeat(${colLevels.length}, minmax(110px,1fr))`,
        }}
        >
            <Cell header />
            {colLevels.map((level) => (
                <Cell header key={`c-${level}`}>{level}</Cell>
            ))}

            {rowSubjects.map((subject) => (
        <React.Fragment key={`r-${subject}`}>
            <Cell header>{subject}</Cell>

            {colLevels.map((level) => {
            const key = `${subject}|||${level}`;
            const isTicked = !!formData.expertise?.[subject]?.includes(level);

            return (
                <Cell
                key={key}
                ticked={isTicked}
                css={{ cursor: 'pointer' }}
                onClick={() => toggle(subject, level)}
                >
                {isTicked ? '✓' : ''}
                </Cell>
            );
            })}
        </React.Fragment>
        ))}
            </Grid>
        </PreviewViewport>
        </div>
        </>
        ) : null}

        <div style={{marginBottom: '3rem'}}/>

        <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>Grades</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>
        
        <ManualField>

        <AddSection>
    <QualificationsDropdown
      value={typeToAdd}
      options={qualificationOptions}
      onChange={setTypeToAdd}
    />
    <LightButton
      type="button"
      onClick={addGroup}
      disabled={isTypeAlreadyAdded}
      css={{
        flexGrow: 1,
        '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
      }}
    >
      <Plus size={15} style={{ marginRight: '0.5rem' }} />
      {isTypeAlreadyAdded ? 'Already Added' : 'Add Qualification'}
    </LightButton>
  </AddSection>

  </ManualField>

  {(formData.qualificationGroups ?? []).map(group => {
    const details = getQualificationDetails(group.type);
    const isSingleScore = (details?.format === 'single-score' || details?.format === 'test-score');
    const ibTotal = group.type === 'IB Diploma' ? calculateIbTotal(group) : null;
    
    return (
      <GroupContainer key={group.id}>
        <GroupHeader>
            <GroupTitle>{group.type.replace(' / IGCSE', '')}</GroupTitle>
            <ActionButton type="button" intent="background" onClick={() => removeGroup(group.id)} aria-label="Remove qualification">
                <Trash2 size={16} stroke="red"/>
            </ActionButton>
        </GroupHeader>
              
        {isSingleScore ? (
          <ManualField>
            <ManualLabel>Score</ManualLabel>
            <ManualInput
                type={details.min !== undefined ? "number" : "text"}
                placeholder={details.min !== undefined ? `Score (e.g. ${details.min} - ${details.max})` : 'Enter Score'}
                value={group.score || ''}
                onChange={e => updateSingleScore(group.id, e.target.value)}
                min={details.min}
                max={details.max}
            />
          </ManualField>
        ) : (
          <>
            <EntryRow 
              css={{ 
                justifyItems: 'start', 
                fontWeight: 'bold', 
                marginBottom: '0.25rem',
                alignItems: 'center',
                display: 'none',
                '@media (min-width: 600px)': {
                      display: 'grid',
                      alignItems: 'center'
                    }            
                }}
              >
                <ManualLabel as="span" css={{ paddingLeft: '0.3rem' }}>
                    Subject
                </ManualLabel>
                <ManualLabel as="span" css={{ paddingLeft: '0.75rem' }}>
                    Grade
                </ManualLabel>
                <span />
              </EntryRow>

            {(group.entries ?? []).map(entry => {
              const subjectOptions = getSubjectOptions(group.type);
              const gradeOptions = getGradeOptions(group.type);

              return (
                <EntryRow key={entry.id}>
                  {subjectOptions.length > 0 ? (
                      <SubjectCombobox
                          mode="single"
                          placeholder="Search or select…"
                          value={entry.subject}
                          onSelect={(newValue) => updateSubjectEntry(group.id, entry.id, 'subject', newValue)}
                          options={subjectOptions}
                      />
                  ) : (
                      <ManualInput type="text" placeholder="e.g. Maths" value={entry.subject} onChange={e => updateSubjectEntry(group.id, entry.id, 'subject', e.target.value)} />
                  )}

                  {gradeOptions.length > 0 ? (
                      <StyledDropdown
                          placeholder="Select..."
                          value={entry.grade}
                          onChange={(newValue) => updateSubjectEntry(group.id, entry.id, 'grade', newValue)}
                          options={gradeOptions}
                      />
                    ) : (
                      <ManualInput type="text" placeholder="Enter Grade" value={entry.grade} onChange={e => updateSubjectEntry(group.id, entry.id, 'grade', e.target.value)} />
                    )}
                  
                  <ActionButton type="button" intent="background" onClick={() => removeSubjectEntry(group.id, entry.id)} aria-label="Remove subject">
                    <Trash2 size={16} stroke="red"/>
                  </ActionButton>
                </EntryRow>
              );
            })}

            <LightButton type="button" onClick={() => addSubjectToGroup(group.id)} css={{marginTop: '1rem'}}>
                <Plus size={15} /> Add Subject
            </LightButton>

            {ibTotal && (
              <TotalScoreContainer>
                <TotalScoreLabel>Total Score:</TotalScoreLabel>
                <TotalScoreValue>{ibTotal.score ?? ''}</TotalScoreValue>
                <TotalScoreBreakdown>{ibTotal.message}</TotalScoreBreakdown>
              </TotalScoreContainer>
            )}
          </>
        )}
      </GroupContainer>
    );
})}

      </CardContent>
      <CardFooter>
        <ActionButton type="button" intent="secondary" onClick={onCancel}>
          Cancel
        </ActionButton>
        <ActionButton
          type="submit"
          intent="primary"
          onClick={handleSaveClick}
          disabled={!isDirty || isSaving || isDescriptionOverLimit}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </ActionButton>
      </CardFooter>
      </form>
      </>
  );
};